import React, { useState, useEffect } from 'react'
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { Modal, Button, Hint, Icon } from 'components'
import { makeStyles } from '@material-ui/core/styles'
import { colors } from '../../../constants'
import { useSelector, useDispatch } from 'react-redux'
import {
  getBankAccountSetupIntent,
  saveBankAccount,
  getBankAccounts,
  getUserCards,
} from '../../../store/actions/payments'

const { WHITE, ERROR } = colors

// Stripe promise using environment variable
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY)

const useStyles = makeStyles({
  modal: {
    width: 648,
    minHeight: 300,
    position: 'relative',
    padding: '40px 40px',
    backgroundColor: WHITE,
    boxShadow: '0 2px 12px 0 rgba(87, 87, 87, 0.15)',
  },
  form: {
    padding: '16px 0',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: '16px 0',
  },
  checkbox: {
    marginRight: '8px',
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  },
  error: {
    color: ERROR,
    padding: '5px 0',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: 8,
  },
  loading: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
})

// Inner form component - works only inside <Elements>
const BankAccountForm = ({ onClose, onSuccess, bankAccountSetupIntent }) => {
  const classes = useStyles()
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()

  const [isDefault, setIsDefault] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [stripeError, setStripeError] = useState('')

  const handleSubmit = async e => {
    e.preventDefault()
    if (!stripe || !elements) return

    setIsSubmitting(true)
    setStripeError('')

    try {
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      })

      if (error) {
        setStripeError(error.message)
        setIsSubmitting(false)
      } else {
        const paymentMethodId = setupIntent.payment_method

        const data = {
          setup_intent_id: bankAccountSetupIntent.setup_intent_id,
          payment_method_id: paymentMethodId,
        }

        await dispatch(saveBankAccount(data))

        dispatch(getBankAccounts())
        dispatch(getUserCards())
        onSuccess()
      }
    } catch (error) {
      setStripeError('An error occurred while saving the bank account')
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className={classes.form}>
      <div style={{ marginBottom: '16px' }}>
        <PaymentElement />
      </div>

      {stripeError && (
        <Hint classes={{ subtitle2: classes.error }}>
          <Icon style={classes.icon} iconClass="las la-exclamation-triangle" />
          {stripeError}
        </Hint>
      )}
      <div className={classes.buttonContainer}>
        <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={!stripe || isSubmitting} isButtonBlocked={isSubmitting}>
          Save Bank Account
        </Button>
      </div>
    </form>
  )
}

// Main component that handles setup intent
const AddBankAccount = ({ isOpen, onClose, bankType }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { bankAccountSetupIntent, bankAccountSetupIntentIsLoading, bankAccountsAreSaving } = useSelector(
    state => state.payments
  )

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen && bankType) {
      const accountType = bankType === 'US bank account' ? 'ach' : 'pad'
      dispatch(getBankAccountSetupIntent(accountType))
    }
  }, [isOpen, dispatch, bankType])

  const handleSuccess = () => {
    onClose()
  }

  const isLoadingState = bankAccountSetupIntentIsLoading || bankAccountsAreSaving || isLoading

  return (
    <Modal open={isOpen} handleClose={onClose} maxWidth={false} dialogClasses={{ paper: classes.modal }}>
      {bankAccountSetupIntentIsLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : bankAccountSetupIntent.client_secret ? (
        <div className={isLoadingState ? classes.loading : ''}>
          <h2 style={{ marginBottom: '16px' }}>Add Bank Account</h2>
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: bankAccountSetupIntent.client_secret,
              link: {
                email: 'andrei.novikau@leverx.com',
              },
              paymentMethodOrder: ['link'],
              fields: {
                termsOfService: {
                  mandateText:
                    "By clicking AGREE AND FINALIZE, you authorize XIRA to credit your account for funds collected on your behalf from your clients or debit charge arising from your use of XIRA services or charge backs & disputes as per XIRA's terms of agreement.",
                },
              },
            }}
          >
            <BankAccountForm
              onClose={onClose}
              onSuccess={handleSuccess}
              bankAccountSetupIntent={bankAccountSetupIntent}
            />
          </Elements>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ marginBottom: '40px' }}>Unable to load bank account setup. Please try again.</p>
          <Button onClick={onClose} variant="primary">
            Close
          </Button>
        </div>
      )}
    </Modal>
  )
}

export default AddBankAccount
